import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';

import {readHistoryList, updateHistoryInfo} from '../../../modules/chat/chat';

import {Box, IconButton, InputBase, List, ListItem, ListItemButton, ListItemText, Typography} from '@mui/material';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import HistoryMenuBox from './history-menu/HistoryMenuBox';
import MessageBox from '../../common/box/MessageBox';

export default function MainContent({ isMiddleSideBar }) {
    const dispatch = useDispatch();

    const location = useLocation();

    const navigate = useNavigate();

    const { listResponse, listErrorResponse } = useSelector(state => state.chat);

    const { listLoading } = useSelector(({ loading }) => ({
        listLoading: loading['chat/READ_HISTORY_LIST']
    }));

    const inputRef = useRef(null);
    const [editingState, setEditingState] = useState({ id: null, title: '', originalTitle: '' });

    const [hoveredHistoryId, setHoveredHistoryId] = useState(null);
    const [historyMenuState, setHistoryMenuState] = useState({ id: null, anchorEl: null });

    const handleClickHistory = (id) => {
        const prefix = location.pathname.startsWith('/idea') ? '/idea' : '/chat';
        navigate(`${prefix}/${id}`);
    };

    const handleClickMoreIcon = (e, id) => {
        e.stopPropagation();

        setHistoryMenuState((prev) => (
            prev.id === id
                ? { id: null, anchorEl: null }
                : { id, anchorEl: e.currentTarget }
        ));
    };

    const handleCloseHistoryMenu = () => {
        setHistoryMenuState({ id: null, anchorEl: null });
    };

    const handleOpenEditMode = (id, title) => {
        setEditingState({ id, title, originalTitle: title });
        handleCloseHistoryMenu();
    };

    const handleChangeTitle = (e) => {
        setEditingState((prev) => ({
            ...prev,
            title: e.target.value.slice(0, 50)
        }));
    };

    const handleBlurEditTitle = () => {
        if (!editingState.id) return;

        const isEmptyTitle = editingState.title.trim() === '';
        const targetTitle = isEmptyTitle ? editingState.originalTitle : editingState.title;

        const isAvailableRequest = isEmptyTitle || targetTitle !== editingState.originalTitle;

        if (isAvailableRequest) {
            dispatch(updateHistoryInfo({ id: editingState.id, data: { title: targetTitle } }));
        }

        setEditingState({ id: null, title: '', originalTitle: '' });
    };

    const handleKeyDownEditTitle = (e) => {
        if (!editingState.id) return;

        if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
        }
    };

    const handleReloadHistoryList = () => {
        dispatch(readHistoryList());
    };

    useEffect(() => {
        if (!isMiddleSideBar) return;
        if (listResponse) return;

        dispatch(readHistoryList());
    }, [dispatch, isMiddleSideBar, listResponse]);

    useEffect(() => {
        if (!editingState.id) return;
        if (!inputRef.current) return;

        inputRef.current.focus();
        inputRef.current.select();
    }, [editingState.id]);

    return (
        <Box sx={{ width: '100%', minHeight: 0, flex: 1, overflow: 'hidden', px: '12px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            {isMiddleSideBar &&
                <Box sx={{ width: '100%', minHeight: 0, flex: 1, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ marginLeft: '8px', fontSize: '14px', fontWeight: 500, lineHeight: '36px', color: 'text.disabled' }}>
                        채팅 목록
                    </Typography>

                    {listLoading && <MessageBox type='LOADING' loadingSize={32} />}

                    {!listLoading && listErrorResponse > 0 &&
                        <MessageBox
                            type='ERROR'
                            Icon={WarningRoundedIcon}
                            message='페이지를 불러오는 중 오류 발생'
                            handleReload={handleReloadHistoryList}
                        />
                    }

                    {!listLoading && listResponse?.length === 0 &&
                        <MessageBox
                            Icon={QuestionAnswerRoundedIcon}
                            message='새 채팅을 눌러 대화를 시작해 보세요'
                        />
                    }

                    {!listLoading && listResponse?.length > 0 &&
                        <List disablePadding sx={{ minHeight: 0, flex: 1, overflowY: 'auto' }}>
                            {listResponse.map((history) => {
                                const { id, title, isPinned } = history;

                                const isActive = location.pathname === `/chat/${id}`;
                                const isHovered = hoveredHistoryId === id;
                                const isOpenHistoryMenu = historyMenuState.id === id;
                                const isEditing = editingState.id === id;

                                const backgroundColor = (isHovered || isOpenHistoryMenu)
                                    ? 'action.hover'
                                    : isActive
                                        ? 'grey.200'
                                        : 'transparent';

                                return (
                                    isEditing
                                        ? <ListItem
                                            key={id}
                                            disablePadding
                                            inputRef={inputRef}
                                            component={InputBase}
                                            value={editingState.title}
                                            onChange={handleChangeTitle}
                                            onBlur={handleBlurEditTitle}
                                            onKeyDown={handleKeyDownEditTitle}
                                            inputProps={{ maxLength: 50 }}
                                            sx={{
                                                minWidth: 0,
                                                paddingLeft: '8px',
                                                backgroundColor: '#FFF',
                                                borderRadius: '10px',
                                                border: '1px solid',
                                                borderColor: 'primary.main',
                                                '& .MuiInputBase-input': {
                                                    typography: 'subtitle3',
                                                    width: '100%',
                                                    padding: '8px 0',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }
                                            }}
                                        />
                                        : <ListItem
                                            key={id}
                                            disablePadding
                                            onMouseEnter={() => setHoveredHistoryId(id)}
                                            onMouseLeave={() => setHoveredHistoryId(null)}
                                            secondaryAction={
                                                isHovered || isOpenHistoryMenu
                                                    ? <IconButton
                                                        size='small'
                                                        edge='end'
                                                        disableRipple
                                                        onClick={(e) => handleClickMoreIcon(e, id)}
                                                        onMouseEnter={() => setHoveredHistoryId(id)}
                                                        onMouseLeave={() => {
                                                            if (!isOpenHistoryMenu) setHoveredHistoryId(null);
                                                        }}
                                                        sx={{ color: 'action.active' }}
                                                    >
                                                        <MoreVertRoundedIcon fontSize='small' />
                                                    </IconButton>
                                                    : isPinned
                                                        ? <IconButton
                                                            size='small'
                                                            edge='end'
                                                            disableRipple
                                                            onMouseEnter={() => setHoveredHistoryId(id)}
                                                            onMouseLeave={() => setHoveredHistoryId(null)}
                                                            sx={{ color: 'action.active' }}
                                                        >
                                                            <PushPinRoundedIcon fontSize='small' />
                                                        </IconButton>
                                                        : null
                                            }
                                        >
                                            <ListItemButton
                                                selected={isActive}
                                                disableRipple
                                                onClick={() => handleClickHistory(id)}
                                                sx={{
                                                    minWidth: 0,
                                                    paddingLeft: '8px',
                                                    borderRadius: '10px',
                                                    backgroundColor: backgroundColor,
                                                    '&.Mui-selected': {
                                                        backgroundColor: backgroundColor
                                                    },
                                                    '&.Mui-selected:hover': {
                                                        backgroundColor: 'action.hover'
                                                    }
                                                }}
                                            >
                                                <ListItemText
                                                    primary={title}
                                                    primaryTypographyProps={{ variant: 'subtitle3', noWrap: true }}
                                                />
                                            </ListItemButton>
                                            <HistoryMenuBox
                                                open={isOpenHistoryMenu}
                                                anchorEl={historyMenuState.anchorEl}
                                                historyId={id}
                                                isPinned={isPinned}
                                                onClickEditTitle={() => handleOpenEditMode(id, title)}
                                                onClose={handleCloseHistoryMenu}
                                            />
                                        </ListItem>
                                );
                            })}
                        </List>
                    }
                </Box>
            }
        </Box>
    );
}
